/**
 * React Query hooks for Authentication
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authService } from "../services/auth.service";
import type { SendOTPRequest, VerifyOTPRequest } from "../types";
import { toast } from "sonner";

export const useSendOTP = () => {
  return useMutation({
    mutationFn: (data: SendOTPRequest) => authService.sendOTP(data),
    onSuccess: (data) => {
      if (data.success) {
        toast.success( "OTP sent to your mobile number");
      } else {
        toast.error(data.message || "Failed to send OTP");
      }
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to send OTP");
    },
  });
};

export const useVerifyOTP = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: VerifyOTPRequest) => authService.verifyOTP(data),
    onSuccess: (data) => {
      if (data.success) {
        toast.success(data.message || "Login successful!");
        // Invalidate all queries to ensure fresh data is loaded after login
        queryClient.invalidateQueries();
        // Refetch all active queries to show latest updates
        queryClient.refetchQueries();
      } else {
        // Show the exact error message from API response
        toast.error(data.message || "Login failed. Please try again.");
      }
    },
    onError: (error: any) => {
      // Handle network errors or HTTP errors
      const errorMessage = error?.message || "Network error occurred";
      toast.error(errorMessage);
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => authService.logout(),
    onSuccess: () => {
      // Clear all queries
      queryClient.clear();
      toast.success("Logged out successfully");
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to logout");
    },
  });
};

export const useRefreshToken = () => {
  return useMutation({
    mutationFn: () => authService.refreshToken(),
    onError: (error: any) => {
      console.error("Token refresh failed:", error);
      // Redirect to login on refresh failure
      authService.logout();
    },
  });
};

